import bannersIcon from "@assets/images/menu-banners-smop-icon.svg";
import categoriesIcon from "@assets/images/menu-categories-smop-icon.svg";
import collectionIcon from "@assets/images/menu-collections-smop-icon.svg";
import discountsIcon from "@assets/images/menu-discount-smop-icon.svg";
import homeIcon from "@assets/images/menu-home-smop-icon.svg";
import mallIcon from "@assets/images/menu-mall-smop-icon.svg";
import ordersIcon from "@assets/images/menu-mother-order-smop-icon.svg";
import motherOrdersIcon from "@assets/images/menu-orders-smop-icon.svg";
import productIcon from "@assets/images/menu-product-smop-icon.svg";
import reportsIcon from "@assets/images/menu-reports-icon.svg";
import sellerIcon from "@assets/images/menu-seller-smop-icon.svg";
import settlementIcon from "@assets/images/menu-settlement-icon.svg";
import storeIcon from "@assets/images/menu-store-smop-icon.svg";
import userIcon from "@assets/images/menu-user-smop-icon.svg";
import voucherIcon from "@assets/images/menu-voucher-smop-icon.svg";
import motorIcon from "@assets/images/motor-1.svg";
import settingsIcon from "@assets/images/settings-icon.svg";
import shoppingBagIcon from "@assets/images/shoppingBagIcon.svg";
import { isSuperUser } from "@saleor/auth/misc";
import { bannerListUrl } from "@saleor/banners/urls";
import { brandsListUrl } from "@saleor/brands/url";
import {
  couponCollectionsSectionUrl,
  couponManageUrl
} from "@saleor/coupons/url";
import { dropOffListUrl } from "@saleor/dropOff/url";
import useUser from "@saleor/hooks/useUser";
import { sectionNames } from "@saleor/intl";
import { remittanceListUrl } from "@saleor/remittance/url";
import { remittanceRunnerListUrl } from "@saleor/remittanceRunner/url";
import { financeOrderListUrl } from "@saleor/reports/PayoutOrders/urls";
import { IntlShape } from "react-intl";

import { categoryListUrl } from "../../categories/urls";
import { collectionListUrl } from "../../collections/urls";
import { customerListUrl } from "../../customers/urls";
import { saleListUrl, voucherListUrl } from "../../discounts/urls";
import { dispatchHistoryListPath } from "../../dispatch/url";
import {
  masterFulfillmentListUrl,
  orderListUrl
} from "../../motherOrders/urls";
import { permissionGroupListUrl } from "../../permissionGroups/urls";
import { productListUrl } from "../../products/urls";
import { staffListUrl } from "../../staff/urls";
import { storeOrderListUrl } from "../../storeOrders/urls";
import { PermissionEnum, SmopUserTypeCode } from "../../types/globalTypes";

export interface IMenuItem {
  ariaLabel: string;
  children?: IMenuItem[];
  excludeFrom?: PermissionEnum[];
  icon?: any;
  label: string;
  permission?: PermissionEnum;
  permissions?: PermissionEnum[];
  testingContextId: string;
  url?: string;
  userType?: SmopUserTypeCode;
}

export const filterMenuStructure = (
  menuStructure: IMenuItem[]
): IMenuItem[] => {
  const { user } = useUser();
  const userPermCodes = user?.userPermissions?.map(perm => perm.code);
  const userType = user?.accessGroup?.smopUsertypeCode;

  const validatePermission = (menuItem: IMenuItem) => {
    const { permission, permissions, ariaLabel } = menuItem;

    if (ariaLabel === "home") {
      return true;
    }

    if (permissions !== undefined) {
      for (const permission of userPermCodes) {
        if (permissions.includes(permission)) {
          return true;
        }
      }
    }

    if (permission !== undefined) {
      return userPermCodes.includes(permission);
    }

    return false;
  };

  return menuStructure
    .filter(
      menuItem =>
        validatePermission(menuItem) ||
        (menuItem.userType && userType === menuItem.userType)
    )
    .filter(
      menuItem =>
        isSuperUser(user) ||
        !menuItem?.excludeFrom?.some(perm => userPermCodes.includes(perm))
    )
    .map(menuItem =>
      menuItem?.children?.length >= 1
        ? { ...menuItem, children: filterMenuStructure(menuItem?.children) }
        : menuItem
    );
};

function createMenuStructure(intl: IntlShape): IMenuItem[] {
  return [
    {
      ariaLabel: "home",
      excludeFrom: [PermissionEnum.MANAGE_PAYOUT],
      icon: homeIcon,
      label: intl.formatMessage(sectionNames.home),
      testingContextId: "home",
      url: "/"
    },
    {
      ariaLabel: "announcementmanagement",
      icon: userIcon,
      label: intl.formatMessage(sectionNames.announcements),
      permission: PermissionEnum.MANAGE_ANNOUNCEMENTS,
      testingContextId: "announcementmanagement",
      url: "/announcements/"
    },
    {
      ariaLabel: "mallmanagement",
      icon: mallIcon,
      label: "Malls",
      permissions: [PermissionEnum.MANAGE_MALLS, PermissionEnum.MODIFY_MALLS],
      testingContextId: "mallmanagement",
      url: "/malls/"
    },
    {
      ariaLabel: "storemanagement",
      icon: storeIcon,
      label: "Stores",
      permissions: [
        PermissionEnum.MANAGE_TENANTS,
        PermissionEnum.MODIFY_TENANTS
      ],
      testingContextId: "storemanagement",
      url: "/store/"
    },
    {
      ariaLabel: "sellermanagement",
      icon: sellerIcon,
      label: "Sellers",
      permission: PermissionEnum.MANAGE_BRANDS,
      testingContextId: "mallmanagement",
      url: "/seller/"
    },
    {
      ariaLabel: "brands",
      icon: storeIcon,
      label: "Brands",
      permission: PermissionEnum.MANAGE_BRANDS,
      testingContextId: "brands",
      url: brandsListUrl()
    },
    {
      ariaLabel: "usermanagement",
      icon: userIcon,
      label: "Users",
      permission: PermissionEnum.MANAGE_STAFF,
      testingContextId: "usermanagement",
      url: staffListUrl()
    },
    {
      ariaLabel: "usertypes",
      icon: userIcon,
      label: "User Types",
      permission: PermissionEnum.MANAGE_USER_TYPES,
      testingContextId: "usertypes",
      url: permissionGroupListUrl()
    },
    {
      ariaLabel: "customers",
      icon: userIcon,
      label: intl.formatMessage(sectionNames.customers),
      permission: PermissionEnum.MANAGE_USERS,
      testingContextId: "customers",
      url: customerListUrl()
    },
    {
      ariaLabel: "products",
      icon: productIcon,
      label: intl.formatMessage(sectionNames.products),
      permission: PermissionEnum.MANAGE_PRODUCTS,
      testingContextId: "products",
      url: productListUrl()
    },
    {
      ariaLabel: "PayoutsTab",
      children: [
        {
          ariaLabel: "payoutOrders",
          label: "Orders",
          permission: PermissionEnum.MANAGE_PAYOUT_OPEN_ORDER,
          testingContextId: "payoutOrders",
          url: financeOrderListUrl()
        },
        {
          ariaLabel: "batch",
          icon: reportsIcon,
          label: "Active Batch",
          permission: PermissionEnum.MANAGE_BATCH,
          testingContextId: "batch",
          url: "/payout/batch"
        },
        {
          ariaLabel: "batchHistory",
          icon: reportsIcon,
          label: "Batch History",
          permission: PermissionEnum.MANAGE_BATCH_HISTORY,
          testingContextId: "batchHistory",
          url: "/payout/batch-history"
        }
      ],
      icon: reportsIcon,
      label: "Payouts",
      permission: PermissionEnum.MANAGE_PAYOUT,
      testingContextId: "Payouts"
      // url: "/payout/finance-orders",
    },
    {
      ariaLabel: "settlement",
      icon: settlementIcon,
      label: "Settlement",
      permission: PermissionEnum.MANAGE_SETTLEMENT,
      testingContextId: "settlement",
      url: "/merchant-settlement-report"
    },
    {
      ariaLabel: "reports",
      icon: reportsIcon,
      label: "Reports",
      permission: PermissionEnum.MANAGE_REPORTS,
      testingContextId: "reports",
      url: "/reports"
    },
    {
      ariaLabel: "categories",
      icon: categoriesIcon,
      label: intl.formatMessage(sectionNames.categories),
      permission: PermissionEnum.MANAGE_CATEGORIES,
      testingContextId: "categories",
      url: categoryListUrl()
    },
    {
      ariaLabel: "collections",
      icon: collectionIcon,
      label: intl.formatMessage(sectionNames.collections),
      permission: PermissionEnum.MANAGE_COLLECTIONS,
      testingContextId: "collections",
      url: collectionListUrl()
    },
    {
      ariaLabel: "orders",
      icon: motherOrdersIcon,
      label: intl.formatMessage(sectionNames.fulfillment),
      permission: PermissionEnum.VIEW_ADMIN_FULFILLMENT,
      testingContextId: "fulfillment",
      url: orderListUrl()
    },
    {
      ariaLabel: "master-fulfillment",
      icon: motherOrdersIcon,
      label: intl.formatMessage(sectionNames.fulfillmentV2),
      permission: PermissionEnum.VIEW_MASTER_FULFILLMENT,
      testingContextId: "master-fulfillment",
      url: masterFulfillmentListUrl()
    },
    {
      ariaLabel: "storeorder",
      icon: ordersIcon,
      label: intl.formatMessage(sectionNames.storeOrder),
      permission: PermissionEnum.VIEW_ADMIN_ORDERS,
      testingContextId: "Orders",
      url: storeOrderListUrl()
    },
    {
      ariaLabel: "dispatch",
      icon: motorIcon,
      label: intl.formatMessage(sectionNames.dispatch),
      permission: PermissionEnum.VIEW_ADMIN_DISPATCH,
      testingContextId: "Dispatch",
      url: dispatchHistoryListPath
    },
    {
      ariaLabel: "dropOff",
      icon: shoppingBagIcon,
      label: intl.formatMessage(sectionNames.dropOff),
      permission: PermissionEnum.VIEW_ADMIN_DROP_OFF,
      testingContextId: "dropOff",
      url: dropOffListUrl()
    },
    {
      ariaLabel: "sales",
      icon: discountsIcon,
      label: intl.formatMessage(sectionNames.sales),
      permission: PermissionEnum.MANAGE_SALES,
      testingContextId: "sales",
      url: saleListUrl()
    },
    {
      ariaLabel: "PromosTab",
      children: [
        {
          ariaLabel: "vouchers",
          label: intl.formatMessage(sectionNames.vouchers),
          permission: PermissionEnum.MANAGE_VOUCHERS,
          testingContextId: "vouchers",
          url: voucherListUrl()
        },
        {
          ariaLabel: "coupons",
          icon: voucherIcon,
          label: intl.formatMessage(sectionNames.coupons),
          permission: PermissionEnum.MANAGE_COUPONS,
          testingContextId: "coupons",
          url: "#coupons-dropdown",
          children: [
            {
              ariaLabel: "Manage_coupons",
              label: intl.formatMessage(sectionNames.manage_coupons),
              permission: PermissionEnum.MANAGE_COUPONS,
              testingContextId: "manage_coupons",
              url: couponManageUrl
            },
            {
              ariaLabel: "coupon_collection",
              label: intl.formatMessage(sectionNames.collections),
              permission: PermissionEnum.MANAGE_COUPONS,
              testingContextId: "coupon_collection",
              url: couponCollectionsSectionUrl
            }
          ]
        }
      ],
      icon: voucherIcon,
      label: "Promos",
      permissions: [
        PermissionEnum.MANAGE_COUPONS,
        PermissionEnum.MANAGE_VOUCHERS
      ],
      testingContextId: "Promos"
    },
    {
      ariaLabel: "banners",
      icon: bannersIcon,
      label: intl.formatMessage(sectionNames.banners),
      permission: PermissionEnum.MANAGE_BANNERS,
      testingContextId: "banners",
      url: bannerListUrl()
    },
    {
      ariaLabel: "cluster",
      icon: settingsIcon,
      label: intl.formatMessage(sectionNames.cluster),
      permission: PermissionEnum.MANAGE_CLUSTERS,
      testingContextId: "cluster",
      url: "/clusters"
    },
    {
      ariaLabel: "paymentEvents",
      excludeFrom: [
        PermissionEnum.MANAGE_STORE_ORDERS,
        PermissionEnum.MANAGE_FINANCE,
        PermissionEnum.MANAGE_ORDERS
      ],
      icon: reportsIcon,
      label: intl.formatMessage(sectionNames.paymentEvents),
      permission: PermissionEnum.MANAGE_REPORTS,
      testingContextId: "paymentEvents",
      url: "/payment-events"
    },

    {
      ariaLabel: "remittanceTab",
      children: [
        {
          ariaLabel: "remittance",
          label: intl.formatMessage(sectionNames.remittance),
          permission: PermissionEnum.MANAGE_ORDERS,
          testingContextId: "remittance",
          url: remittanceListUrl()
        },
        {
          ariaLabel: "remittanceRunner",
          label: intl.formatMessage(sectionNames.remittanceRunner),
          permission: PermissionEnum.MANAGE_REMITTANCE,
          testingContextId: "remittanceRunner",
          url: remittanceRunnerListUrl()
        }
      ],
      icon: reportsIcon,
      label: intl.formatMessage(sectionNames.remittance),
      permission: PermissionEnum.MANAGE_REMITTANCE,
      testingContextId: "remittanceTab"
    },
    {
      ariaLabel: "sellerOrders",
      icon: reportsIcon,
      label: intl.formatMessage(sectionNames.sellerOrders),
      permission: PermissionEnum.MANAGE_SELLER_ORDER_REPORTS,
      testingContextId: "sellerOrders",
      url: "/seller-orders"
    }
  ];
}

export const isActiveMenuItemUrl = (
  menuItemUrl: string,
  location: string
): boolean => {
  if (menuItemUrl === "/" && location === "/") {
    // home('/') page url case
    return true;
  } else if (menuItemUrl !== "/") {
    // Handling of special cases
    if (
      location.split("/")[1] === "coupons" &&
      menuItemUrl.split("/")[1] === "discounts" &&
      menuItemUrl.split("/")[2] === "vouchers?"
    ) {
      // Special case to highlight promos tab when user is on Coupons page.
      return true;
    }
    if (
      location.split("/")[1] === "discounts" &&
      location.split("/")[2] === "bulk-vouchers" &&
      menuItemUrl.split("/")[1] === "discounts" &&
      menuItemUrl.split("/")[2] === "vouchers?"
    ) {
      // Special case to highlight promos tab when user is on bulk-vouchers page.
      return true;
    }
    if (
      menuItemUrl === "/remittance-runner/remittances" &&
      (location === "/remittance-runner/remittances" ||
        location === "/remittance")
    ) {
      // Special case where multiple pages is associated to a single tab.
      return true;
    }
    if (
      location.split("/")[1] === "discounts" &&
      menuItemUrl.split("/")[1] === "discounts"
    ) {
      // Handling of /discounts routing (e.g. /discounts/sales, /discounts/vouchers)
      return (
        location.split("/")[2] === menuItemUrl.split("/")[2].replace("?", "")
      );
    }

    return location.split("/")[1] === menuItemUrl.split("/")[1];
  }
  return false;
};

export default createMenuStructure;
